import { Tag } from 'antd';
import dayjs from 'dayjs';

type ProductItem = {
  name: string;
  category: string;
  mainCategory?: string;
  brand?: string;
  presentation?: string;
  baskets?: string[];
  currentPrice?: number | null;
  previousPrice?: number | null;
  currentPriceReportedTime?: number | null;
  previousPriceReportedTime?: number | null;
  priceChange?: number | null;
  priceChangePercent?: number | null;
  onProductClick?: (productName: string) => void;
  t: (key: string) => string;
};

const ProductItem = (item: ProductItem) => {
  const getPriceTrend = () => {
    if (
      item.priceChange === null ||
      item.priceChange === undefined ||
      item.priceChangePercent === null ||
      item.priceChangePercent === undefined
    ) {
      return null;
    }

    return {
      change: item.priceChange,
      percentage: Math.abs(item.priceChangePercent),
      isIncrease: item.priceChange > 0,
      isDecrease: item.priceChange < 0,
    };
  };

  const getTrendIcon = (trend: { isIncrease?: boolean; isDecrease?: boolean } | null) => {
    if (trend?.isIncrease) return '↗️';
    if (trend?.isDecrease) return '↘️';
    return '→';
  };

  const priceTrend = getPriceTrend();

  return (
    <div className="product-item mb-4 rounded-lg border border-border-color bg-white p-6">
      <div className="flex w-full flex-col gap-3 mmd:flex-row mmd:justify-between">
        <div className="flex flex-col gap-2 mmd:flex-row mmd:items-center">
          <h1
            className="item-name cursor-pointer text-lg font-semibold transition-colors hover:text-blue-600"
            onClick={() => item.onProductClick?.(item.name)}
          >
            {item.name}
          </h1>
          <Tag className="w-fit rounded-full px-4 py-1 text-xs font-semibold text-black mmd:mx-3">
            {item.mainCategory && item.mainCategory !== 'Unknown' ? item.mainCategory : 'General'}
          </Tag>
        </div>
        {item.currentPrice && (
          <div className="flex items-center gap-2 hidden mmd:flex">
            <div className="text-left mmd:text-right space-y-1">
              <div className="text-lg font-semibold text-gray-900">
                L. {Number(item?.currentPrice || 0).toFixed(2)}
              </div>
              {item.currentPriceReportedTime && (
                <div className="text-xs text-gray-500">
                  {dayjs(Number(item.currentPriceReportedTime)).format('YYYY-MM-DD hh:mm:ss A')}
                </div>
              )}
              {priceTrend && (
                <div
                  className={`flex items-center gap-1 text-sm ${
                    priceTrend.isIncrease
                      ? 'text-red-600'
                      : priceTrend.isDecrease
                        ? 'text-green-600'
                        : 'text-gray-500'
                  }`}
                >
                  <span className="text-xs">{getTrendIcon(priceTrend)}</span>
                  <span>
                    {priceTrend?.isIncrease ? '+' : ''}
                    {priceTrend?.percentage?.toFixed(1) || '0.0'}%
                  </span>
                  <span className="text-xs opacity-75">
                    ({priceTrend?.isIncrease ? '+' : ''}L.{' '}
                    {Math.abs(priceTrend?.change || 0).toFixed(2)})
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-col md:flex-row flex-wrap gap-4 text-sm text-gray-600">
        {item.brand && item.brand.trim() && item.brand !== 'null' && (
          <><span className="font-semibold">{item.t('brand')}:</span> <span className='text-gray-500'> {item.brand}</span></>
        )}
        {item.presentation && item.presentation.trim() && item.presentation !== 'null' && (
          <><span className="font-semibold">{item.t('presentation')}:</span> <span className='text-gray-500'> {item.presentation}</span></>
        )}
        {item.baskets && item.baskets.length > 0 && (
          <><span className="font-semibold">{item.t('basket')}:</span> <span className='text-gray-500'> {item.baskets.join(', ')}</span></>
        )}
        {item.previousPrice && (
          <><span className="font-semibold">
            {item.t('previous')}: </span> <span className='text-gray-500'>L. {Number(item.previousPrice || 0).toFixed(2)}
            {item.previousPriceReportedTime && (
              <span className="ml-1 text-xs">
                ({dayjs(Number(item.previousPriceReportedTime)).format('YYYY-MM-DD hh:mm:ss A')})
              </span>
            )}
          </span></>
        )}
      </div>
      {item.currentPrice && (
          <div className="flex items-center gap-2 mmd:hidden mt-5">
            <div className="text-left mmd:text-right">
              <div className="text-lg font-semibold text-gray-900">
                L. {Number(item?.currentPrice || 0).toFixed(2)}
              </div>
              {item.currentPriceReportedTime && (
                <div className="text-xs text-gray-500">
                  {dayjs(Number(item.currentPriceReportedTime)).format('YYYY-MM-DD hh:mm:ss A')}
                </div>
              )}
              {priceTrend && (
                <div
                  className={`flex items-center gap-1 text-sm ${
                    priceTrend.isIncrease
                      ? 'text-red-600'
                      : priceTrend.isDecrease
                        ? 'text-green-600'
                        : 'text-gray-500'
                  }`}
                >
                  <span className="text-xs">{getTrendIcon(priceTrend)}</span>
                  <span>
                    {priceTrend?.isIncrease ? '+' : ''}
                    {priceTrend?.percentage?.toFixed(1) || '0.0'}%
                  </span>
                  <span className="text-xs opacity-75">
                    ({priceTrend?.isIncrease ? '+' : ''}L.{' '}
                    {Math.abs(priceTrend?.change || 0).toFixed(2)})
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default ProductItem;
